import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { apiRequest, configureApiClient } from '../api/client';
import type { AdminProfile, LoginResponse } from '../api/types';
import { clearSession, loadSession, saveSession, type Session } from './session';

interface AuthContextValue {
  admin: AdminProfile | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session, setSession] = useState<Session | null>(() => loadSession());
  // O cliente HTTP lê o token por aqui, sem depender de re-render.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const logout = useCallback(() => {
    clearSession();
    setSession(null);
    queryClient.clear();
  }, [queryClient]);

  // Na renderização, e não num efeito: os efeitos dos filhos rodam antes dos do
  // pai, e as primeiras consultas sairiam sem token. A atribuição é idempotente.
  configureApiClient({
    token: () => {
      const current = sessionRef.current;

      return current !== null && current.expiresAt > Date.now() ? current.token : null;
    },
    unauthorized: logout,
  });

  const login = useCallback(async (email: string, password: string) => {
    const response = await apiRequest<LoginResponse>('POST', '/admin/auth/login', {
      email,
      password,
    });

    const next: Session = {
      token: response.access_token,
      admin: response.admin,
      expiresAt: Date.now() + response.expires_in * 1000,
    };

    saveSession(next);
    setSession(next);
  }, []);

  const value = useMemo(
    () => ({ admin: session?.admin ?? null, login, logout }),
    [session, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);

  if (context === null) {
    throw new Error('useAuth precisa estar dentro de <AuthProvider>');
  }

  return context;
}
